import type { EditorView } from '@codemirror/view';
import { EditorView as CMEditorView } from '@codemirror/view';

/**
 * Shared scroll/position mapping helpers used by content-aware scroll sync
 * (split view) and the outline panel's click-to-jump.
 *
 * The single source of truth for alignment is the markdown **source line**:
 * the preview stamps every block element with `data-line="N"` (1-based source
 * line, via the core pipeline's `rehypeSourceLines`), and CodeMirror knows the
 * source line of any viewport position. So plaintext↔preview sync maps the line
 * at the top of one viewport to the matching `[data-line]` block (or CM pos) on
 * the other side — aligning the *visible content*, not a naive scroll ratio.
 *
 * Milkdown's WYSIWYG ProseMirror DOM carries no source positions, so for
 * wysiwyg↔preview we fall back to a block-index mapping: the i-th top-level
 * block in the editor aligns to the i-th top-level block in the preview body.
 *
 * All helpers are defensive: missing elements / empty docs / jsdom (no real
 * layout) return safe no-ops or 1, so component tests don't blow up.
 */

const PREVIEW_BLOCK_SELECTOR = '[data-line]';

/** Whether the user has asked the OS to minimize non-essential motion. */
export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

/**
 * Animate an element's `scrollTop` to `top` with a quick ease-out tween.
 * Falls back to an instant set when reduced-motion is requested or when
 * rAF/`performance` are unavailable (e.g. jsdom). Safe to call repeatedly:
 * any in-flight tween for the same element is cancelled first.
 */
const activeTweens = new WeakMap<HTMLElement, number>();

export function smoothScrollTo(el: HTMLElement | null, top: number, ms = 200): void {
  if (!el) return;
  const max = el.scrollHeight - el.clientHeight;
  const target = Math.max(0, Math.min(Number.isFinite(max) ? max : top, top));

  const existing = activeTweens.get(el);
  if (existing != null && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(existing);
    activeTweens.delete(el);
  }

  if (
    prefersReducedMotion() ||
    typeof requestAnimationFrame !== 'function' ||
    typeof performance === 'undefined'
  ) {
    el.scrollTop = target;
    return;
  }

  const start = el.scrollTop;
  const delta = target - start;
  if (Math.abs(delta) < 1 || ms <= 0) {
    el.scrollTop = target;
    return;
  }

  const t0 = performance.now();
  const step = (now: number): void => {
    const p = Math.min(1, (now - t0) / ms);
    // ease-out cubic
    const eased = 1 - Math.pow(1 - p, 3);
    el.scrollTop = start + delta * eased;
    if (p < 1) {
      activeTweens.set(el, requestAnimationFrame(step));
    } else {
      activeTweens.delete(el);
    }
  };
  activeTweens.set(el, requestAnimationFrame(step));
}

/** 1-based source line of the topmost (partially) visible CodeMirror line. */
export function editorTopLine(view: EditorView | null): number {
  if (!view) return 1;
  try {
    const rect = view.scrollDOM.getBoundingClientRect();
    // Position at the top-left of the viewport; `false` clamps to the nearest
    // valid position rather than returning null between lines.
    const pos = view.posAtCoords({ x: rect.left + 1, y: rect.top + 1 }, false);
    // Fall back to the rendered viewport start if coords aren't usable (jsdom).
    const usePos = typeof pos === 'number' ? pos : view.viewport.from;
    return view.state.doc.lineAt(usePos).number;
  } catch {
    return 1;
  }
}

/** Scroll the CodeMirror editor so `line` (1-based) is at the top (instant). */
export function scrollEditorToLine(view: EditorView | null, line: number): void {
  if (!view) return;
  try {
    const doc = view.state.doc;
    const clamped = Math.max(1, Math.min(doc.lines, Math.round(line)));
    const pos = doc.line(clamped).from;
    view.dispatch({
      effects: CMEditorView.scrollIntoView(pos, { y: 'start' }),
    });
  } catch {
    /* layout/measure unavailable (jsdom) — no-op */
  }
}

/**
 * Place the caret at the start of `line` (1-based) AND scroll the line into
 * view. Used after opening a file from a search content-match so the user can
 * keep typing right where the match was. Also focuses the editor so keystrokes
 * go to the new caret position immediately.
 */
export function placeCursorAtLine(view: EditorView | null, line: number): void {
  if (!view) return;
  try {
    const doc = view.state.doc;
    const clamped = Math.max(1, Math.min(doc.lines, Math.round(line)));
    const pos = doc.line(clamped).from;
    view.dispatch({
      selection: { anchor: pos, head: pos },
      effects: CMEditorView.scrollIntoView(pos, { y: 'center' }),
    });
    view.focus();
  } catch {
    /* layout/measure unavailable (jsdom) — no-op */
  }
}

/**
 * Smooth-scroll the CodeMirror editor so `line` (1-based) sits at the top, by
 * tweening `scrollDOM.scrollTop` toward the line's measured offset. Falls back
 * to the instant `scrollEditorToLine` if coordinates can't be measured.
 */
export function scrollEditorToLineSmooth(view: EditorView | null, line: number): void {
  if (!view) return;
  try {
    const doc = view.state.doc;
    const clamped = Math.max(1, Math.min(doc.lines, Math.round(line)));
    const pos = doc.line(clamped).from;
    const coords = view.coordsAtPos(pos, 1);
    const scroller = view.scrollDOM;
    if (!coords) {
      scrollEditorToLine(view, line);
      return;
    }
    const top = coords.top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    smoothScrollTo(scroller, top);
  } catch {
    scrollEditorToLine(view, line);
  }
}

interface DataLineBlock {
  el: HTMLElement;
  line: number;
}

/** Collect preview blocks carrying `data-line`, ascending by source line. */
function previewLineBlocks(previewEl: HTMLElement): DataLineBlock[] {
  const out: DataLineBlock[] = [];
  const nodes = previewEl.querySelectorAll<HTMLElement>(PREVIEW_BLOCK_SELECTOR);
  nodes.forEach((el) => {
    const raw = el.getAttribute('data-line');
    const line = raw ? Number(raw) : NaN;
    if (Number.isFinite(line)) out.push({ el, line });
  });
  out.sort((a, b) => a.line - b.line);
  return out;
}

/** Vertical offset of `el` relative to the scroll container's content top. */
function offsetWithin(container: HTMLElement, el: HTMLElement): number {
  return el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
}

/**
 * Scroll the preview so the content at source `line` sits at the viewport top.
 * Finds the block with the greatest `data-line` ≤ line and interpolates toward
 * the next block by line fraction (so scrolling through a long paragraph or
 * code block tracks smoothly rather than snapping block-to-block).
 */
export function scrollPreviewToLine(previewEl: HTMLElement | null, line: number): void {
  if (!previewEl) return;
  const blocks = previewLineBlocks(previewEl);
  if (blocks.length === 0) return;

  // Find the last block whose data-line is ≤ the requested line.
  let i = 0;
  for (let k = 0; k < blocks.length; k += 1) {
    if (blocks[k].line <= line) i = k;
    else break;
  }
  const cur = blocks[i];
  const next = blocks[i + 1];
  let target = offsetWithin(previewEl, cur.el);

  if (next && next.line > cur.line) {
    const frac = Math.max(0, Math.min(1, (line - cur.line) / (next.line - cur.line)));
    const nextTop = offsetWithin(previewEl, next.el);
    target += (nextTop - target) * frac;
  }

  const max = previewEl.scrollHeight - previewEl.clientHeight;
  previewEl.scrollTop = Math.max(0, Math.min(max, target));
}

/** Source `data-line` of the preview block nearest the top of the viewport. */
export function previewTopLine(previewEl: HTMLElement | null): number {
  if (!previewEl) return 1;
  const blocks = previewLineBlocks(previewEl);
  if (blocks.length === 0) return 1;

  const viewportTop = previewEl.scrollTop;
  // The last block whose top is at or above the viewport top is the one
  // currently anchoring the visible content.
  let best = blocks[0];
  for (const b of blocks) {
    const top = offsetWithin(previewEl, b.el);
    if (top <= viewportTop + 1) best = b;
    else break;
  }
  return best.line;
}

/** Locate the preview heading element whose `id === slug`. */
function previewSlugTarget(previewEl: HTMLElement, slug: string): HTMLElement | null {
  // CSS.escape may be unavailable in some test environments.
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(slug)
      : slug.replace(/["\\#.:>~+*^$[\]()|]/g, '\\$&');
  return previewEl.querySelector<HTMLElement>(`#${escaped}`);
}

/** Scroll the preview to the heading element with `id === slug` (instant). */
export function scrollPreviewToSlug(previewEl: HTMLElement | null, slug: string): void {
  if (!previewEl || !slug) return;
  const target = previewSlugTarget(previewEl, slug);
  if (!target) return;
  const top = offsetWithin(previewEl, target);
  const max = previewEl.scrollHeight - previewEl.clientHeight;
  previewEl.scrollTop = Math.max(0, Math.min(max, top));
}

/** Smooth-scroll the preview to the heading element with `id === slug`. */
export function scrollPreviewToSlugSmooth(previewEl: HTMLElement | null, slug: string): void {
  if (!previewEl || !slug) return;
  const target = previewSlugTarget(previewEl, slug);
  if (!target) return;
  smoothScrollTo(previewEl, offsetWithin(previewEl, target));
}

/** `id` of the preview heading nearest (at/above) the top of the viewport. */
export function previewTopSlug(previewEl: HTMLElement | null, offset = 4): string | null {
  if (!previewEl) return null;
  try {
    const headings = previewEl.querySelectorAll<HTMLElement>(
      '.mp-preview__body :is(h1, h2, h3, h4, h5, h6)[id]',
    );
    if (headings.length === 0) return null;
    const viewportTop = previewEl.scrollTop + offset;
    let best: string | null = headings[0].id || null;
    for (const h of headings) {
      const top = offsetWithin(previewEl, h);
      if (top <= viewportTop) best = h.id || best;
      else break;
    }
    return best;
  } catch {
    return null;
  }
}

/* ---------- WYSIWYG (block-index) mapping ----------
   ProseMirror carries no source positions, so we align by structural block
   index: the i-th top-level block of `.milkdown .ProseMirror` corresponds to
   the i-th top-level child of `.mp-preview__body`. */

function wysiwygBlocks(editorEl: HTMLElement | null): HTMLElement[] {
  if (!editorEl) return [];
  const pm = editorEl.querySelector<HTMLElement>('.milkdown .ProseMirror') ?? editorEl.querySelector<HTMLElement>('.ProseMirror');
  if (!pm) return [];
  return Array.from(pm.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
}

function previewBodyBlocks(previewEl: HTMLElement | null): HTMLElement[] {
  if (!previewEl) return [];
  const body = previewEl.querySelector<HTMLElement>('.mp-preview__body') ?? previewEl;
  return Array.from(body.children).filter((c): c is HTMLElement => c instanceof HTMLElement);
}

/** Index of the top-level block nearest the top of a scroll container. */
function topBlockIndex(scroller: HTMLElement, blocks: HTMLElement[]): number {
  if (blocks.length === 0) return 0;
  const viewportTop = scroller.scrollTop;
  let best = 0;
  for (let i = 0; i < blocks.length; i += 1) {
    const top = offsetWithin(scroller, blocks[i]);
    if (top <= viewportTop + 1) best = i;
    else break;
  }
  return best;
}

/** Scroll a container so the block at `index` sits at its viewport top. */
function scrollToBlockIndex(scroller: HTMLElement, blocks: HTMLElement[], index: number): void {
  if (blocks.length === 0) return;
  const clamped = Math.max(0, Math.min(blocks.length - 1, index));
  const top = offsetWithin(scroller, blocks[clamped]);
  const max = scroller.scrollHeight - scroller.clientHeight;
  scroller.scrollTop = Math.max(0, Math.min(max, top));
}

/** Block index nearest the top of the WYSIWYG editor's ProseMirror viewport. */
export function wysiwygTopBlockIndex(editorEl: HTMLElement | null): number {
  if (!editorEl) return 0;
  return topBlockIndex(editorEl, wysiwygBlocks(editorEl));
}

/** Block index nearest the top of the preview viewport. */
export function previewTopBlockIndex(previewEl: HTMLElement | null): number {
  if (!previewEl) return 0;
  return topBlockIndex(previewEl, previewBodyBlocks(previewEl));
}

/** Scroll the WYSIWYG editor so its i-th top-level block is at the top. */
export function scrollWysiwygToBlockIndex(editorEl: HTMLElement | null, index: number): void {
  if (!editorEl) return;
  scrollToBlockIndex(editorEl, wysiwygBlocks(editorEl), index);
}

/** Scroll the preview so its i-th top-level block is at the top. */
export function scrollPreviewToBlockIndex(previewEl: HTMLElement | null, index: number): void {
  if (!previewEl) return;
  scrollToBlockIndex(previewEl, previewBodyBlocks(previewEl), index);
}

/**
 * Scroll the WYSIWYG editor to its i-th heading (h1–h6 in document order).
 * Used by the outline panel's click-to-jump in the WYSIWYG view, where headings
 * have no slug/source-line — we align by heading index instead.
 */
export function scrollWysiwygToHeadingIndex(editorEl: HTMLElement | null, index: number): void {
  if (!editorEl) return;
  const pm =
    editorEl.querySelector<HTMLElement>('.milkdown .ProseMirror') ??
    editorEl.querySelector<HTMLElement>('.ProseMirror');
  if (!pm) return;
  const headings = pm.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
  const target = headings[Math.max(0, Math.min(headings.length - 1, index))];
  if (!target) return;
  // The Crepe scroll container is the editor root we were handed.
  const top = offsetWithin(editorEl, target);
  const max = editorEl.scrollHeight - editorEl.clientHeight;
  editorEl.scrollTop = Math.max(0, Math.min(max, top));
}

/**
 * Index (0-based, in document order) of the WYSIWYG heading nearest the top of
 * the editor viewport — the heading whose top is at/above the viewport top.
 * Returns -1 when no heading is at/above the top (or there are none).
 */
export function wysiwygTopHeadingIndex(editorEl: HTMLElement | null, offset = 4): number {
  if (!editorEl) return -1;
  try {
    const pm =
      editorEl.querySelector<HTMLElement>('.milkdown .ProseMirror') ??
      editorEl.querySelector<HTMLElement>('.ProseMirror');
    if (!pm) return -1;
    const headings = pm.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
    if (headings.length === 0) return -1;
    const viewportTop = editorEl.scrollTop + offset;
    let best = -1;
    for (let i = 0; i < headings.length; i += 1) {
      const top = offsetWithin(editorEl, headings[i]);
      if (top <= viewportTop) best = i;
      else break;
    }
    return best;
  } catch {
    return -1;
  }
}

/**
 * Smooth variant of {@link scrollWysiwygToHeadingIndex}. ProseMirror's DOM is
 * the real scroll content, so we use the heading element's native
 * `scrollIntoView` (respecting reduced-motion) — falling back to instant when
 * unavailable (jsdom).
 */
export function scrollWysiwygToHeadingIndexSmooth(
  editorEl: HTMLElement | null,
  index: number,
): void {
  if (!editorEl) return;
  const pm =
    editorEl.querySelector<HTMLElement>('.milkdown .ProseMirror') ??
    editorEl.querySelector<HTMLElement>('.ProseMirror');
  if (!pm) return;
  const headings = pm.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
  const target = headings[Math.max(0, Math.min(headings.length - 1, index))];
  if (!target) return;
  try {
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  } catch {
    scrollWysiwygToHeadingIndex(editorEl, index);
  }
}
